import React, { useEffect, useState } from "react";
import { adminList, adminCreate, adminUpdate, adminDelete, adminUpload, API } from "@/lib/api";
import { SCHEMAS } from "./schema";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, Eye, EyeSlash, Image as ImageIcon } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ImageField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const r = await adminUpload(file); onChange(`${BACKEND_URL}${r.url}`); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div>
      {value && <img src={value} alt="" className="mb-2 h-28 w-full rounded-md border border-slate-200 object-cover" />}
      <div className="flex gap-2">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm hover:border-gold">
          <ImageIcon size={16} /> {busy ? "…" : "Upload"}
          <input type="file" className="hidden" accept="image/*" onChange={onFile} />
        </label>
      </div>
    </div>
  );
}

function FileField({ value, onChange, accept }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { const r = await adminUpload(file); onChange(`${BACKEND_URL}${r.url}`); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      {value && <a href={value} target="_blank" rel="noreferrer" className="mb-2 block truncate text-xs text-gold-dark">{value}</a>}
      <div className="flex gap-2">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="File URL" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm hover:border-gold">
          {busy ? "…" : "Upload"}
          <input type="file" className="hidden" accept={accept} onChange={onFile} />
        </label>
      </div>
    </div>
  );
}

function Editor({ collection, item, onClose, onSaved }) {
  const schema = SCHEMAS[collection];
  const [form, setForm] = useState(() => {
    const base = {};
    schema.fields.forEach((f) => {
      let v = item ? item[f.name] : (f.type === "boolean" ? true : "");
      if (f.type === "json" && v && typeof v !== "string") v = JSON.stringify(v, null, 2);
      base[f.name] = v ?? "";
    });
    base.published = item ? item.published !== false : true;
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [refs, setRefs] = useState({});
  useEffect(() => {
    schema.fields.filter((f) => f.type === "reference").forEach((f) => {
      adminList(f.refCollection).then((d) => setRefs((p) => ({ ...p, [f.name]: d }))).catch(() => {});
    });
    // eslint-disable-next-line
  }, [collection]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const payload = { ...form };
    for (const f of schema.fields) {
      if (f.type === "json") {
        try { payload[f.name] = form[f.name] ? JSON.parse(form[f.name]) : (f.name === "faqs" ? [] : {}); }
        catch { toast.error(`Invalid JSON in ${f.label}`); return; }
      }
    }
    setSaving(true);
    try {
      if (item) await adminUpdate(collection, item.id, payload);
      else await adminCreate(collection, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10">
      <div className="w-full max-w-2xl rounded-lg bg-white p-8" data-testid="content-editor">
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-serif text-2xl text-navy">{item ? "Edit" : "Add"} — {schema.label}</h3>
          <button onClick={onClose} data-testid="editor-close"><X size={22} className="text-navy/60" /></button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {schema.fields.map((f) => (
            <div key={f.name} className={f.big ? "" : ""}>
              <label className="mb-1.5 block text-[13px] font-medium text-navy/80">{f.label}</label>
              {f.type === "image" ? <ImageField value={form[f.name]} onChange={(v) => set(f.name, v)} /> :
               f.type === "file" ? <FileField value={form[f.name]} onChange={(v) => set(f.name, v)} accept={f.accept} /> :
               f.type === "textarea" || f.type === "json" ? (
                <textarea data-testid={`edit-${f.name}`} rows={f.big ? 8 : 3} readOnly={f.readonly} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-gold ${f.type === "json" ? "font-mono text-xs" : ""}`} />
              ) : f.type === "reference" ? (
                <select data-testid={`edit-${f.name}`} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                  <option value="">Select…</option>{(refs[f.name] || []).map((o) => <option key={o.id} value={o.id}>{o.title || o.name || o.label}</option>)}
                </select>
              ) : f.type === "select" ? (
                <select data-testid={`edit-${f.name}`} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                  <option value="">Select…</option>{f.options.map((o) => <option key={o}>{o}</option>)}
                </select>
              ) : (
                <input data-testid={`edit-${f.name}`} readOnly={f.readonly} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-gold" />
              )}
            </div>
          ))}
          <label className="flex items-center gap-2 text-sm text-navy/80">
            <input type="checkbox" data-testid="edit-published" checked={form.published} onChange={(e) => set("published", e.target.checked)} /> Published (visible on site)
          </label>
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid="editor-save" onClick={save} disabled={saving} className="rounded-md bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-700 disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
          <button onClick={onClose} className="rounded-md border border-slate-300 px-6 py-2.5 text-sm font-semibold text-navy">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function ContentManager({ collection }) {
  const schema = SCHEMAS[collection];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit

  const load = () => { setLoading(true); adminList(collection).then(setItems).catch(() => setItems([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [collection]);

  const remove = async (it) => {
    if (!window.confirm("Delete this item?")) return;
    await adminDelete(collection, it.id); toast.success("Deleted"); load();
  };
  const togglePub = async (it) => { await adminUpdate(collection, it.id, { published: it.published === false }); load(); };

  const titleField = schema.fields.find((f) => ["title", "name", "label", "key"].includes(f.name))?.name || schema.fields[0].name;

  return (
    <div data-testid={`manager-${collection}`}>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-3xl text-navy">{schema.label}</h2>
          <p className="text-sm text-softgrey-600">{items.length} item{items.length !== 1 ? "s" : ""}</p>
        </div>
        {!schema.singletonKey && (
          <button data-testid="add-item" onClick={() => setEditing(null)} className="flex items-center gap-2 rounded-md bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-700"><Plus size={16} /> Add New</button>
        )}
      </div>
      {loading ? <p className="text-softgrey-600">Loading…</p> : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 last:border-0 hover:bg-softgrey-100">
                  <td className="px-5 py-3">
                    <div className="font-medium text-navy line-clamp-1">{String(it[titleField] || it.key || "Untitled")}</div>
                    {it.year && <span className="text-xs text-softgrey-400">{it.year}</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button title="Toggle publish" onClick={() => togglePub(it)} className="rounded p-1.5 text-navy/50 hover:bg-slate-200">{it.published === false ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                      <button data-testid={`edit-${it.id}`} onClick={() => setEditing(it)} className="rounded p-1.5 text-navy/60 hover:bg-slate-200"><PencilSimple size={16} /></button>
                      {!schema.singletonKey && <button onClick={() => remove(it)} className="rounded p-1.5 text-red-500 hover:bg-red-50"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td className="px-5 py-8 text-center text-softgrey-600">No items yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {editing !== undefined && <Editor collection={collection} item={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
